import { getStatusAtivoLabel } from '../../../modules/types/StatusAtivoEnum';
import { EnumOption } from '../../../modules/types/types';
import { valoresExibidos } from '../../../pages/daf/ProcessosAquisitivos/acts/planoTrabalho/auxiliarPlanoTrabalho';
import { valoresExibidosOffCanvasTeds } from '../../../pages/daf/ProcessosAquisitivos/teds/auxiliarTeds';
import { valoresExibidosPlanoAcao } from '../../../pages/daf/ProcessosAquisitivos/teds/planoAcao/auxiliarPlanoAcao';
import { getAllPlanosAcaoTed } from '../../../services/teds/tedsPlanoAcaoService';
import { getAllPlanosTrabalhoTed } from '../../../services/teds/tedsPlanoTrabalhoService';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';
import {
  addFiscaisAquisicaoSheet,
  addPartesEnvolvidasSheet,
  addResponsaveisAquisicaoSheet,
  addTermosAditivosSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';

export class TedExportPreparer implements DataPreparer {
  private modulo = 'ted';

  /**
   * Busca os relacionados de TED
   */
  private async fetchRelatedData(tedIds: number[]): Promise<any> {
    if (tedIds.length === 0) return {};

    try {
      const [allPlanosAcao, allPlanosTrabalho] = await Promise.all([
        this.fetchTedPlanosAcao(),
        this.fetchTedPlanosDeTrabalho(),
      ]);
      const planosAcao = this.filterByTedIds(allPlanosAcao, tedIds);
      const planosTrabalho = this.filterByTedIds(allPlanosTrabalho, tedIds);

      const [
        partesEnvolvidas,
        fiscais,
        responsaveis,
        termosAditivos,
        tramitacoesSei,
      ] = await Promise.all([
        fetchGenericEntityData('parteenvolvida', this.modulo, tedIds),
        fetchGenericEntityData('fiscalaquisicao', this.modulo, tedIds),
        fetchGenericEntityData('responsavelaquisicao', this.modulo, tedIds),
        fetchGenericEntityData('termoaditivo', this.modulo, tedIds),
        fetchGenericEntityData('tramitacoes', this.modulo, tedIds),
      ]);

      return {
        planosAcao,
        planosTrabalho,
        partesEnvolvidas,
        fiscais,
        responsaveis,
        termosAditivos,
        tramitacoesSei,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados de TED:', error);
      return {};
    }
  }

  private async fetchTedPlanosDeTrabalho(): Promise<any[]> {
    try {
      const result = await getAllPlanosTrabalhoTed();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar Planos de Trabalho do TED:', error);
      return [];
    }
  }

  private async fetchTedPlanosAcao(): Promise<any[]> {
    try {
      const result = await getAllPlanosAcaoTed();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar Planos de Ação do TED:', error);
      return [];
    }
  }

  private filterByTedIds(data: any[], tedsIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do Ted através de duas formas possíveis
      const tedId = item.ted?.id || item.tedId || item.tedPlanoAcao.ted?.id;
      return tedsIds.includes(tedId);
    });
  }

  async prepareForExport(
    selectedTeds: any[],
    additionalData?: {
      statusTed?: EnumOption[];
      componenteAf?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const tedIds = selectedTeds.map((t) => t.id);

    const { statusTed = [], componenteAf = [] } = additionalData || {};

    // 1. Sheet principal - TED
    sheets.push({
      name: 'Termos de Exec. Descentralizada',
      data: selectedTeds.map((ted) =>
        this.transformTed(ted, statusTed, componenteAf),
      ),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosOffCanvasTeds,
        dataDou: 'Data D.O.U.',
        link: 'Link',
        documentoSei: 'Documento SEI',
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(tedIds);

    // 3. Adicionar sheets relacionados
    this.addPlanosAcaoSheet(sheets, relatedData.planosAcao);
    this.addPlanosTrabalhoSheet(sheets, relatedData.planosTrabalho);
    addPartesEnvolvidasSheet(sheets, this.modulo, relatedData.partesEnvolvidas);
    addFiscaisAquisicaoSheet(sheets, this.modulo, relatedData.fiscais);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveis,
    );
    addTermosAditivosSheet(sheets, this.modulo, relatedData.termosAditivos);
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoesSei);

    return sheets;
  }

  /**
   * Transformação principal
   */
  private transformTed = (
    ted: any,
    statusTed: EnumOption[],
    componenteAf: EnumOption[],
  ) => {
    return {
      // logs
      id: ted.id,
      usuarioRegistro: ted.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: ted.dataRegistro,
      dataUltimaAtualizacao: ted.dataUltimaAtualizacao,
      usuarioAtualizacao: ted.usuarioAtualizacao?.nomeCompleto || 'N/A',
      usuarioUltimaVisualizacao:
        ted.usuarioUltimaVisualizacao?.nomeCompleto || 'N/A',
      nEdicoes: ted.nEdicoes || 0,
      nVisualizacoes: ted.nVisualizacoes || 0,
      nVisualizadores: ted.nVisualizadores || 0,

      // módulo
      status: statusTed[ted.status]?.label || ted.status,
      componenteAf: componenteAf[ted.componenteAf]?.label || ted.componenteAf,
      numeroTed: ted.numeroTed || 'N/A',
      documentoSei: ted.documentoSei || 'N/A',
      nupSeiFase1: ted.nupSeiFase1 || 'N/A',
      nupSeiFase2: ted.nupSeiFase2 || 'N/A',
      dataInicio: ted.dataInicio || 'N/A',
      dataTermino: ted.dataTermino || 'N/A',
      dataDou: ted.dataDou || 'N/A',
      link: ted.link || 'N/A',
      observacoes: ted.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Planos de Trabalho se houver dados
   */
  private addPlanosTrabalhoSheet(sheets: ExportSheet[], planos?: any[]) {
    if (!planos?.length) return;

    sheets.push({
      name: 'Planos de Trabalho',
      data: planos.map(this.transformPlanoTrabalho),
      columns: {
        ...baseLogColumns,
        ...valoresExibidos,
        produtoDAF: 'Produto Farmacêutico',
        tedId: 'ID TED',
        tedPlanoAcaoId: 'ID PLANO AÇÃO TED',
        numeroPa: 'Nº Plano de Ação',
      },
    });
  }

  private transformPlanoTrabalho = (plano: any) => {
    return {
      //logs
      id: plano.id,
      usuarioRegistro: plano.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: plano.dataRegistro,
      dataUltimaAtualizacao: plano.dataUltimaAtualizacao,
      usuarioAtualizacao: plano.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: plano.nEdicoes || 0,
      nVisualizacoes: plano.nVisualizacoes || 0,
      nVisualizadores: plano.nVisualizadores || 0,

      //Dados da TED
      tedId: plano.tedPlanoAcao.ted.id || 'N/A',
      tedPlanoAcaoId: plano.tedPlanoAcao.id || 'N/A',
      numeroPa: plano.tedPlanoAcao.ted.numeroPa || 'N/A',
      produtoDAF: plano.produtoDAF.produtoFarmaceutico,

      //Dados do Plano da TED
      embalagemFator: plano.embalagemFator ? plano.embalagemFator : '0',

      //Quantidades
      qtdAdministrativa: plano.qtdAdministrativa
        ? plano.qtdAdministrativa
        : '0',
      qtdEstrategico: plano.qtdEstrategico ? plano.qtdEstrategico : '0',
      qtdJudicial: plano.qtdJudicial ? plano.qtdJudicial : '0',
      qtdOutro: plano.qtdOutro ? plano.qtdOutro : '0',

      //Valores
      valorUnitHomologado: plano.valorUnitHomologado
        ? plano.valorUnitHomologado
        : '',
      valorUnitReequilibrio:
        plano.valorReequilibrioBin === 'S'
          ? plano.valorUnitReequilibrio
          : 'N/A',
      observacoes: plano.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Planos de Ação se houver dados
   */
  private addPlanosAcaoSheet(sheets: ExportSheet[], planos?: any[]) {
    if (!planos?.length) return;

    sheets.push({
      name: 'Planos de Ação',
      data: planos.map(this.transformPlanoAcao),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosPlanoAcao,
        tedId: 'ID TED',
      },
    });
  }

  private transformPlanoAcao = (planoAcao: any) => {
    return {
      //logs
      id: planoAcao.id,
      usuarioRegistro: planoAcao.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: planoAcao.dataRegistro,
      dataUltimaAtualizacao: planoAcao.dataUltimaAtualizacao,
      usuarioAtualizacao: planoAcao.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: planoAcao.nEdicoes || 0,
      nVisualizacoes: planoAcao.nVisualizacoes || 0,
      nVisualizadores: planoAcao.nVisualizadores || 0,

      //Dados da TED
      tedId: planoAcao.ted.id || 'N/A',
      status: getStatusAtivoLabel(planoAcao.status) || 'N/A',
      numeroPa: planoAcao.numeroPa || 'N/A',
      documentoSei: planoAcao.documentoSei || 'N/A',
      dataInicio: planoAcao.dataInicio ? planoAcao.dataInicio : 'Não informado',
      dataTermino: planoAcao.dataTermino
        ? planoAcao.dataTermino
        : 'Não informado',
      objetivo: planoAcao.objetivo,
      justificativa: planoAcao.justificativa,
      nPlanosTrabalho: parseFloat(planoAcao.nPlanosTrabalho),
      nProdutosDaf: parseFloat(planoAcao.nProdutosDaf),
      valorTotal: parseFloat(planoAcao.valorTotal),
      observacoes: planoAcao.observacoes || '',
    };
  };
}
